import React, { useState, useRef, useEffect } from 'react';
import { useColorMode } from '@chakra-ui/react';

const languages: any = [
  { code: "en", name: "English", flag: "https://flagcdn.com/w320/us.png" },
  { code: "pt-BR", name: "Português (Brasil)", flag: "https://flagcdn.com/w320/br.png" },
  { code: "hr", name: "Hrvatski", flag: "https://flagcdn.com/w320/hr.png" },
];

export default function LanguagePicker({ onChange, initialLanguage, className }: any) {
  const [selectedLanguage, setSelectedLanguage] = useState(
    languages.find((lang: any) => lang.code === initialLanguage) || languages[0]
  );
  const [isMenuOpened, setMenuOpened] = useState(false);
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';
  const listRef = useRef<HTMLUListElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const handleLanguageChange = (language: any) => {
    setSelectedLanguage(language);
    onChange(language.code);
    setMenuOpened(false);
    triggerRef.current?.focus();
  };

  // Focus first item when menu opens
  useEffect(() => {
    if (isMenuOpened) {
      const firstBtn = listRef.current?.querySelector<HTMLButtonElement>('button');
      firstBtn?.focus();
    }
  }, [isMenuOpened]);

  const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
      e.preventDefault();
      setMenuOpened(true);
    }
  };

  const handleItemKeyDown = (e: React.KeyboardEvent, lang: any, index: number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleLanguageChange(lang);
    } else if (e.key === 'Escape') {
      setMenuOpened(false);
      triggerRef.current?.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const btns = listRef.current?.querySelectorAll<HTMLButtonElement>('button');
      btns?.[index + 1]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (index === 0) {
        setMenuOpened(false);
        triggerRef.current?.focus();
      } else {
        const btns = listRef.current?.querySelectorAll<HTMLButtonElement>('button');
        btns?.[index - 1]?.focus();
      }
    }
  };

  return (
    <div className={className} style={{ position: "relative", display: "inline-block" }}>
      <button
        ref={triggerRef}
        id="language-picker"
        aria-haspopup="listbox"
        aria-expanded={isMenuOpened}
        aria-label={`Language: ${selectedLanguage.name}`}
        style={{
          display: "flex",
          alignItems: "center",
          padding: "10px",
          fontSize: "16px",
          borderRadius: "5px",
          cursor: "pointer",
          backgroundColor: isDark ? "#1e293b" : "#f9f9f9",
          border: isDark ? "1px solid #334155" : "1px solid #ccc",
          color: isDark ? "#f1f5f9" : undefined,
          minWidth: "80px",
        }}
        onClick={() => setMenuOpened(o => !o)}
        onKeyDown={handleTriggerKeyDown}
      >
        <img
          src={selectedLanguage.flag}
          alt=""
          style={{ width: 20, height: 14, marginRight: 10 }}
        />
        <span style={{ marginLeft: 10 }}>{isMenuOpened ? '▲' : '▼'}</span>
      </button>
      <ul
        ref={listRef}
        role="listbox"
        aria-label="Select language"
        style={{
          listStyle: "none",
          padding: 0,
          margin: 0,
          position: "absolute",
          top: "100%",
          left: 0,
          backgroundColor: isDark ? "#1e293b" : "#fff",
          border: isDark ? "1px solid #334155" : "1px solid #ccc",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          zIndex: 20000,
          display: isMenuOpened ? 'block' : 'none',
          overflowY: "auto",
          maxHeight: "200px",
          width: '150px'
        }}
        id="language-options"
      >
        {languages.map((lang: any, index: number) => (
          <li key={lang.code} role="option" aria-selected={lang.code === selectedLanguage.code}>
            <button
              onClick={() => handleLanguageChange(lang)}
              onKeyDown={(e) => handleItemKeyDown(e, lang, index)}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "10px",
                cursor: "pointer",
                transition: "background-color 0.2s",
                width: "100%",
                background: "none",
                border: "none",
                fontSize: "inherit",
                textAlign: "left",
                color: isDark ? "#f1f5f9" : undefined,
              }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = isDark ? "#334155" : "#f0f0f0")}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
            >
              <img
                src={lang.flag}
                alt=""
                style={{ width: 20, height: 14, marginRight: 10 }}
              />
              {lang.name}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
